import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { Clock3, Filter, RefreshCw, Save } from 'lucide-react'
import type {
  JLocomoBenchmarkSlice,
  JLocomoCurationPacketDetail,
  JLocomoReadinessResponse,
  JLocomoCurationReviewStatus,
} from '@/types/api'
import {
  useCompileJLocomoCuration,
  useJLocomoArtifactsStatus,
  useJLocomoCurationOverview,
  useJLocomoCurationPacket,
  useJLocomoCurationPackets,
  useJLocomoCurationQueue,
  useJLocomoReadiness,
  useRunJLocomoSmokeBenchmark,
  useUpdateJLocomoCurationPacket,
  useValidateJLocomoCuration,
} from '@/hooks/useJLocomoCuration'

const STATUS_OPTIONS: Array<JLocomoCurationReviewStatus | 'all'> = ['all', 'draft', 'reviewed', 'adjudicated']
const SLICE_OPTIONS: Array<JLocomoBenchmarkSlice | 'all'> = [
  'all',
  'single-session',
  'cross-session',
  'temporal',
  'multihop',
  'persona-consistency',
]
const SPLIT_OPTIONS: Array<'all' | 'train' | 'eval'> = ['all', 'train', 'eval']

const STATUS_LABELS: Record<JLocomoCurationReviewStatus | 'all', string> = {
  all: 'すべて',
  draft: '下書き',
  reviewed: 'レビュー済み',
  adjudicated: '確定',
}

const SLICE_LABELS: Record<JLocomoBenchmarkSlice | 'all', string> = {
  all: 'すべて',
  'single-session': '単一セッション',
  'cross-session': '複数セッション',
  temporal: '時間条件あり',
  multihop: '複数根拠',
  'persona-consistency': '人物像の一貫性',
}

const SPLIT_LABELS: Record<'all' | 'train' | 'eval', string> = {
  all: 'すべて',
  train: 'train',
  eval: 'eval',
}

const PRIORITY_LABELS = {
  temporal: '時間条件の確認',
  'cross-session': '複数セッション確認',
  persona: '人物像の一貫性確認',
  standard: '標準確認',
} as const

const QUEUE_ACTION_LABELS = {
  review: 'レビュー',
  adjudicate: '裁定',
  'spot-check': '点検',
} as const

const RELATION_OPTIONS = [
  { value: 'within_window', label: '期間の中にあるか' },
  { value: 'latest_before_reference', label: '基準時点より前の最新か' },
  { value: 'aggregate_over_window', label: '期間を通した集計か' },
  { value: 'deadline_by_window_end', label: '締切が期間末までか' },
] as const

type FormState = {
  reviewStatus: JLocomoCurationReviewStatus
  benchmarkSlices: JLocomoBenchmarkSlice[]
  query: string
  expectedAnswer: string
  evidenceDocumentIds: string
  mustHitEvidence: string
  requiredFacts: string
  notes: string
  referenceTime: string
  targetWindowStart: string
  targetWindowEnd: string
  expectedRelation: typeof RELATION_OPTIONS[number]['value']
  allowAbstain: boolean
  timeZone: string
}

export function JLocomoCurationPage() {
  const [status, setStatus] = useState<JLocomoCurationReviewStatus | 'all'>('draft')
  const [slice, setSlice] = useState<JLocomoBenchmarkSlice | 'all'>('all')
  const [split, setSplit] = useState<'all' | 'train' | 'eval'>('all')
  const [query, setQuery] = useState('')
  const [selectedPacketId, setSelectedPacketId] = useState('')
  const [form, setForm] = useState<FormState | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [strictPilotValidation, setStrictPilotValidation] = useState(true)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [smokeSuite, setSmokeSuite] = useState<'recall' | 'answer-quality'>('recall')
  const [smokeDryRun, setSmokeDryRun] = useState(false)
  const [smokeQueryCount, setSmokeQueryCount] = useState(5)

  const overviewQuery = useJLocomoCurationOverview()
  const artifactsQuery = useJLocomoArtifactsStatus()
  const readinessQuery = useJLocomoReadiness()
  const queueQuery = useJLocomoCurationQueue(20, false)
  const packetsQuery = useJLocomoCurationPackets({
    status,
    slice,
    split,
    q: query,
    limit: 50,
    offset: 0,
  })
  const detailQuery = useJLocomoCurationPacket(selectedPacketId)
  const updateMutation = useUpdateJLocomoCurationPacket(selectedPacketId)
  const validateMutation = useValidateJLocomoCuration()
  const compileMutation = useCompileJLocomoCuration()
  const smokeMutation = useRunJLocomoSmokeBenchmark()

  useEffect(() => {
    const firstPacketId = packetsQuery.data?.items[0]?.packetId ?? ''
    if (!selectedPacketId && firstPacketId) {
      setSelectedPacketId(firstPacketId)
    }
    if (
      selectedPacketId &&
      packetsQuery.data &&
      !packetsQuery.data.items.some((item) => item.packetId === selectedPacketId)
    ) {
      setSelectedPacketId(firstPacketId)
    }
  }, [packetsQuery.data, selectedPacketId])

  useEffect(() => {
    if (!detailQuery.data?.packet) return
    setForm(toFormState(detailQuery.data.packet))
    setSaveError(null)
  }, [detailQuery.data?.packet])

  const summaryCards = useMemo(() => {
    const breakdown = overviewQuery.data?.statusBreakdown
    return [
      { label: '下書き', value: breakdown?.draft ?? 0 },
      { label: 'レビュー済み', value: breakdown?.reviewed ?? 0 },
      { label: '確定', value: breakdown?.adjudicated ?? 0 },
    ]
  }, [overviewQuery.data])

  const handleSave = async () => {
    if (!selectedPacketId || !form) return
    setSaveError(null)

    if (form.benchmarkSlices.length === 0) {
      setSaveError('少なくとも 1 つの slice を選んでください。')
      return
    }

    if (parseMultiline(form.evidenceDocumentIds).length === 0) {
      setSaveError('少なくとも 1 つの evidence document ID を入れてください。')
      return
    }

    const hasTemporal = form.benchmarkSlices.includes('temporal')
    if (hasTemporal && (!form.referenceTime || !form.targetWindowStart || !form.targetWindowEnd)) {
      setSaveError('時間条件ありの問題では、基準時刻と対象期間を入れてください。')
      return
    }

    try {
      await updateMutation.mutateAsync({
        reviewStatus: form.reviewStatus,
        benchmarkSlices: form.benchmarkSlices,
        query: form.query,
        expectedAnswer: form.expectedAnswer,
        evidenceDocumentIds: parseMultiline(form.evidenceDocumentIds),
        mustHitEvidence: parseMultiline(form.mustHitEvidence),
        requiredFacts: parseMultiline(form.requiredFacts),
        temporalSpec: hasTemporal
          ? {
              referenceTime: form.referenceTime,
              targetWindow: {
                start: form.targetWindowStart,
                end: form.targetWindowEnd,
              },
              expectedRelation: form.expectedRelation,
              allowAbstain: form.allowAbstain,
              timeZone: form.timeZone || undefined,
            }
          : null,
        notes: form.notes,
      })
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました。')
    }
  }

  const detail = detailQuery.data
  const context = detail?.conversationContext

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">J-LoCoMo 整備</h1>
          <p className="text-muted-foreground">
            日本語の長期会話ベンチマークを育てる専用画面です。質問文だけでなく、根拠、時間条件、会話の前後関係をまとめて確認できます。
          </p>
        </div>
        <button
          onClick={() => {
            void overviewQuery.refetch()
            void artifactsQuery.refetch()
            void readinessQuery.refetch()
            void queueQuery.refetch()
            void packetsQuery.refetch()
            if (selectedPacketId) {
              void detailQuery.refetch()
            }
          }}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
        >
          <RefreshCw className="h-4 w-4" />
          更新
        </button>
      </div>

      <section className="rounded-xl border bg-card p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
            運用ガイド
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <GuideCard
              title="1. 問い方を自然にする"
              body="query は評価のための人工的な文ではなく、人がそのまま聞きそうな日本語に整えます。"
            />
            <GuideCard
              title="2. 根拠を少なく強くする"
              body="evidence は答えを支える最小限の発話に絞り、絶対に拾ってほしいものだけ must-hit に残します。"
            />
            <GuideCard
              title="3. 時間条件を曖昧にしない"
              body="temporal は『いつを基準に、どの期間を見て、何を判定するか』まで埋めます。"
            />
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-5">
        <StatCard label="会話数" value={overviewQuery.data?.conversationCount ?? '-'} />
        <StatCard label="質問数" value={overviewQuery.data?.packetCount ?? '-'} />
        {summaryCards.map((card) => (
          <StatCard key={card.label} label={card.label} value={card.value} />
        ))}
      </div>

      <section className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium">Readiness</div>
            <p className="mt-1 text-sm text-muted-foreground">
              readiness は、pilot が公開品質へ近づいているかを示す準備完了度です。特に「裁定済みの割合」と「生成モデルと採点モデルが分かれているか」を見ます。
            </p>
          </div>
          {readinessQuery.data ? (
            <div className="rounded-full border px-3 py-1 text-xs font-medium">
              eval 裁定率 {Math.round(readinessQuery.data.reviewProgress.evalAdjudicatedRate * 100)}%
            </div>
          ) : null}
        </div>

        {readinessQuery.data ? <ReadinessSection readiness={readinessQuery.data} /> : null}
      </section>

      <section className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium">Compile / Validate</div>
            <p className="mt-1 text-sm text-muted-foreground">
              compile は編集済み packet を重ねて benchmark 用ファイルを作る処理です。validate は不足や矛盾を検査します。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={strictPilotValidation}
                onChange={(e) => setStrictPilotValidation(e.target.checked)}
              />
              pilot の最低件数も厳密に検査する
            </label>
            <button
              onClick={() => {
                setActionMessage(null)
                void validateMutation.mutateAsync(strictPilotValidation).then((result) => {
                  setActionMessage(
                    result.effectiveIssues.length === 0
                      ? 'validate 完了: 問題は見つかりませんでした。'
                      : `validate 完了: ${result.effectiveIssues.length} 件の issue があります。`,
                  )
                }).catch((error) => {
                  setActionMessage(error instanceof Error ? error.message : 'validate に失敗しました。')
                })
              }}
              disabled={validateMutation.isPending}
              className="rounded-md border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
            >
              {validateMutation.isPending ? 'validate 中…' : 'Validate'}
            </button>
            <button
              onClick={() => {
                setActionMessage(null)
                void compileMutation.mutateAsync().then((result) => {
                  setActionMessage(`compile 完了: ${result.report.qaCount} QA を出力しました。`)
                }).catch((error) => {
                  setActionMessage(error instanceof Error ? error.message : 'compile に失敗しました。')
                })
              }}
              disabled={compileMutation.isPending}
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {compileMutation.isPending ? 'compile 中…' : 'Compile'}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <MetaCard label="source" value={artifactsQuery.data?.sourceExists ? 'あり' : 'なし'} />
          <MetaCard label="packets" value={artifactsQuery.data?.packetsExists ? 'あり' : 'なし'} />
          <MetaCard label="compiled files" value={artifactsQuery.data?.compiledExists ? 'あり' : 'なし'} />
          <MetaCard
            label="last compile"
            value={artifactsQuery.data?.report?.generatedAt ? formatDate(artifactsQuery.data.report.generatedAt) : '未実行'}
          />
        </div>

        {artifactsQuery.data?.report?.report ? (
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <MetaCard label="compiled conversations" value={String(artifactsQuery.data.report.report.conversationCount)} />
            <MetaCard label="compiled sessions" value={String(artifactsQuery.data.report.report.sessionCount)} />
            <MetaCard label="compiled turns" value={String(artifactsQuery.data.report.report.turnCount)} />
            <MetaCard label="compiled QA" value={String(artifactsQuery.data.report.report.qaCount)} />
          </div>
        ) : null}

        {actionMessage ? (
          <div className="mt-4 rounded-md border bg-background px-4 py-3 text-sm">{actionMessage}</div>
        ) : null}

        {validateMutation.data?.effectiveIssues && validateMutation.data.effectiveIssues.length > 0 ? (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
            <div className="text-sm font-medium">validate で見つかった issue</div>
            <ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">
              {validateMutation.data.effectiveIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium">Benchmark Smoke Run</div>
            <p className="mt-1 text-sm text-muted-foreground">
              smoke run は小さなサンプルで benchmark 実行経路が生きているかを確かめる確認です。まず recall、次に必要なら answer-quality を試します。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={smokeSuite}
              onChange={(e) => setSmokeSuite(e.target.value as 'recall' | 'answer-quality')}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="recall">recall</option>
              <option value="answer-quality">answer-quality</option>
            </select>
            <input
              type="number"
              min={1}
              max={20}
              value={smokeQueryCount}
              onChange={(e) => setSmokeQueryCount(Number(e.target.value) || 1)}
              className="w-24 rounded-md border bg-background px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={smokeDryRun}
                onChange={(e) => setSmokeDryRun(e.target.checked)}
              />
              dry run
            </label>
            <button
              onClick={() => {
                setActionMessage(null)
                void smokeMutation.mutateAsync({
                  suite: smokeSuite,
                  dryRun: smokeDryRun,
                  queryCount: smokeQueryCount,
                }).then((result) => {
                  setActionMessage(`smoke run 完了: ${result.suite} を ${result.queryCount} 問で実行しました。`)
                }).catch((error) => {
                  setActionMessage(error instanceof Error ? error.message : 'smoke run に失敗しました。')
                })
              }}
              disabled={smokeMutation.isPending}
              className="rounded-md border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
            >
              {smokeMutation.isPending ? '実行中…' : 'Run Smoke'}
            </button>
          </div>
        </div>

        {smokeMutation.data ? (
          <div className="mt-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-4">
              <MetaCard label="suite" value={smokeMutation.data.suite} />
              <MetaCard label="queries" value={String(smokeMutation.data.queryCount)} />
              <MetaCard label="extraction docs" value={String(smokeMutation.data.extractionCount)} />
              <MetaCard label="dry run" value={smokeMutation.data.dryRun ? 'yes' : 'no'} />
            </div>
            <SmokeModelSummary response={smokeMutation.data} />
            <div className="rounded-md border bg-background p-4">
              <div className="text-sm font-medium">metrics</div>
              <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                {JSON.stringify(smokeMutation.data.result.metrics, null, 2)}
              </pre>
            </div>
            <div className="rounded-md border bg-background p-4">
              <div className="text-sm font-medium">details</div>
              <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                {JSON.stringify(smokeMutation.data.result.details, null, 2)}
              </pre>
            </div>
          </div>
        ) : null}
      </section>

      <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
        <section className="space-y-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <Filter className="h-4 w-4" />
              絞り込み
            </div>
            <div className="space-y-3">
              <Field label="状態">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as JLocomoCurationReviewStatus | 'all')}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {STATUS_LABELS[option]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="問題タイプ">
                <select
                  value={slice}
                  onChange={(e) => setSlice(e.target.value as JLocomoBenchmarkSlice | 'all')}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {SLICE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {SLICE_LABELS[option]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="split">
                <select
                  value={split}
                  onChange={(e) => setSplit(e.target.value as 'all' | 'train' | 'eval')}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {SPLIT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {SPLIT_LABELS[option]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="検索">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="質問文 / conversation / evidence"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </Field>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-medium">優先キュー</div>
              <div className="text-xs text-muted-foreground">{queueQuery.data?.queue.length ?? 0} 件</div>
            </div>
            <div className="space-y-2">
              {queueQuery.data?.queue.map((entry) => (
                <button
                  key={entry.packetId}
                  onClick={() => setSelectedPacketId(entry.packetId)}
                  className="w-full rounded-md border p-3 text-left hover:bg-muted"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      #{entry.rank} · {QUEUE_ACTION_LABELS[entry.queueAction]}
                    </span>
                    <span className="text-xs text-muted-foreground">{STATUS_LABELS[entry.reviewStatus]}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium line-clamp-2">{entry.query}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.benchmarkSlices.map((sliceItem) => (
                      <Badge key={sliceItem} label={SLICE_LABELS[sliceItem]} />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 text-sm font-medium">質問一覧</div>
            <div className="space-y-2">
              {packetsQuery.data?.items.map((item) => (
                <button
                  key={item.packetId}
                  onClick={() => setSelectedPacketId(item.packetId)}
                  className={`w-full rounded-md border p-3 text-left hover:bg-muted ${
                    item.packetId === selectedPacketId ? 'border-primary bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-muted-foreground">
                      {item.conversationTitle} / {item.qaId}
                    </div>
                    <div className="text-xs text-muted-foreground">{PRIORITY_LABELS[item.priorityBand]}</div>
                  </div>
                  <div className="mt-1 text-sm font-medium line-clamp-2">{item.query}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.benchmarkSlices.map((sliceItem) => (
                      <Badge key={sliceItem} label={SLICE_LABELS[sliceItem]} />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-4">
          {!detail || !form ? (
            <div className="rounded-lg border bg-card p-8 text-sm text-muted-foreground">
              左の一覧から質問を選ぶと、会話の前後関係を見ながら編集できます。
            </div>
          ) : (
            <>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      {detail.packet.conversationTitle} / {detail.packet.qaId} / {detail.packet.split}
                    </div>
                    <h2 className="mt-1 text-lg font-semibold">{detail.packet.query}</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {detail.packet.benchmarkSlices.map((sliceItem) => (
                      <Badge key={sliceItem} label={SLICE_LABELS[sliceItem]} />
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <MetaCard label="状態" value={STATUS_LABELS[detail.packet.reviewStatus]} />
                  <MetaCard
                    label="優先理由"
                    value={detail.queueMeta ? PRIORITY_LABELS[detail.queueMeta.priorityBand] : '標準確認'}
                  />
                  <MetaCard
                    label="更新日時"
                    value={detail.packet.updatedAt ? formatDate(detail.packet.updatedAt) : 'まだ保存なし'}
                  />
                </div>
              </div>

              <div className="rounded-lg border bg-card p-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="レビュー状態">
                    <select
                      value={form.reviewStatus}
                      onChange={(e) =>
                        setForm((current) =>
                          current ? { ...current, reviewStatus: e.target.value as JLocomoCurationReviewStatus } : current
                        )
                      }
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      {STATUS_OPTIONS.filter((option) => option !== 'all').map((option) => (
                        <option key={option} value={option}>
                          {STATUS_LABELS[option]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="ベンチマーク slice">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {SLICE_OPTIONS.filter((option) => option !== 'all').map((option) => (
                        <label key={option} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={form.benchmarkSlices.includes(option)}
                            onChange={() => toggleSlice(option, setForm)}
                          />
                          {SLICE_LABELS[option]}
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>

                <div className="mt-4 grid gap-4">
                  <Field label="質問文">
                    <textarea
                      value={form.query}
                      onChange={(e) => updateForm(setForm, { query: e.target.value })}
                      rows={3}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label="正答">
                    <textarea
                      value={form.expectedAnswer}
                      onChange={(e) => updateForm(setForm, { expectedAnswer: e.target.value })}
                      rows={4}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                  <div className="grid gap-4 lg:grid-cols-3">
                    <Field label="evidence document IDs">
                      <textarea
                        value={form.evidenceDocumentIds}
                        onChange={(e) => updateForm(setForm, { evidenceDocumentIds: e.target.value })}
                        rows={6}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                      />
                    </Field>
                    <Field label="must-hit evidence">
                      <textarea
                        value={form.mustHitEvidence}
                        onChange={(e) => updateForm(setForm, { mustHitEvidence: e.target.value })}
                        rows={6}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                      />
                    </Field>
                    <Field label="required facts">
                      <textarea
                        value={form.requiredFacts}
                        onChange={(e) => updateForm(setForm, { requiredFacts: e.target.value })}
                        rows={6}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                  </div>

                  <Field label="メモ">
                    <textarea
                      value={form.notes}
                      onChange={(e) => updateForm(setForm, { notes: e.target.value })}
                      rows={3}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                </div>
              </div>

              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <Clock3 className="h-4 w-4" />
                  時間条件
                </div>
                <p className="mb-4 text-sm text-muted-foreground">
                  temporal は「いつを基準に、どの期間を見て、何を判定するか」を固定する欄です。
                </p>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="基準時刻">
                    <input
                      value={form.referenceTime}
                      onChange={(e) => updateForm(setForm, { referenceTime: e.target.value })}
                      placeholder="2025-04-30T09:00:00+09:00"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label="判定ルール">
                    <select
                      value={form.expectedRelation}
                      onChange={(e) =>
                        updateForm(setForm, {
                          expectedRelation: e.target.value as typeof RELATION_OPTIONS[number]['value'],
                        })
                      }
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      {RELATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="対象期間開始">
                    <input
                      value={form.targetWindowStart}
                      onChange={(e) => updateForm(setForm, { targetWindowStart: e.target.value })}
                      placeholder="2025-04-01T00:00:00+09:00"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label="対象期間終了">
                    <input
                      value={form.targetWindowEnd}
                      onChange={(e) => updateForm(setForm, { targetWindowEnd: e.target.value })}
                      placeholder="2025-04-30T23:59:59+09:00"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label="タイムゾーン">
                    <input
                      value={form.timeZone}
                      onChange={(e) => updateForm(setForm, { timeZone: e.target.value })}
                      placeholder="Asia/Tokyo"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label="断定を控える余地">
                    <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.allowAbstain}
                        onChange={(e) => updateForm(setForm, { allowAbstain: e.target.checked })}
                      />
                      根拠が弱いときは答えを控えてよい
                    </label>
                  </Field>
                </div>
              </div>

              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 text-sm font-medium">会話の見取り図</div>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {context?.outline.map((session) => (
                    <div key={session.sessionId} className="rounded-md border p-3">
                      <div className="text-xs text-muted-foreground">
                        {session.sessionId} / {formatDate(session.occurredAt)}
                      </div>
                      <div className="mt-1 text-sm font-medium">{session.title}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {session.isEvidenceSession ? <Badge label="evidence" tone="primary" /> : null}
                        {session.isFocusSession ? <Badge label="focus" /> : null}
                        <Badge label={`${session.turnCount} turns`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 text-sm font-medium">Adjudication 差分</div>
                <p className="mb-4 text-sm text-muted-foreground">
                  adjudication は最終裁定です。ここでは元の問題と現在の編集結果を見比べて、どこを変えたかを確認できます。
                </p>
                {detail.fieldDiffs.length === 0 ? (
                  <div className="rounded-md border bg-background px-4 py-3 text-sm text-muted-foreground">
                    元データからの変更はまだありません。
                  </div>
                ) : (
                  <div className="space-y-4">
                    {detail.fieldDiffs.map((diff) => (
                      <div key={diff.field} className="rounded-md border p-4">
                        <div className="text-sm font-medium">{DIFF_FIELD_LABELS[diff.field] ?? diff.field}</div>
                        <div className="mt-3 grid gap-3 lg:grid-cols-2">
                          <DiffColumn title="source" value={diff.sourceValue} />
                          <DiffColumn title="current" value={diff.currentValue} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 text-sm font-medium">前後文脈</div>
                <div className="space-y-4">
                  {context?.focusSessions.map((session) => (
                    <div key={session.sessionId} className="rounded-lg border p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-xs text-muted-foreground">
                            {session.sessionId} / {formatDate(session.occurredAt)}
                          </div>
                          <div className="text-sm font-medium">{session.title}</div>
                        </div>
                        {session.isEvidenceSession ? <Badge label="根拠を含む session" tone="primary" /> : null}
                      </div>
                      <div className="mt-3 space-y-2">
                        {session.turns.map((turn) => (
                          <div
                            key={turn.documentId}
                            className={`rounded-md border p-3 ${
                              turn.isMustHit
                                ? 'border-primary bg-primary/5'
                                : turn.isEvidence
                                  ? 'border-amber-300 bg-amber-50'
                                  : ''
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                              <span>
                                {turn.speakerName} / {turn.turnId}
                              </span>
                              <span className="font-mono">{turn.documentId}</span>
                            </div>
                            <div className="mt-2 text-sm leading-6">{turn.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {saveError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {saveError}
                </div>
              ) : null}

              <div className="flex justify-end">
                <button
                  onClick={() => void handleSave()}
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {updateMutation.isPending ? '保存中…' : '保存'}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function toFormState(packet: JLocomoCurationPacketDetail): FormState {
  return {
    reviewStatus: packet.reviewStatus,
    benchmarkSlices: packet.benchmarkSlices,
    query: packet.query,
    expectedAnswer: packet.expectedAnswer,
    evidenceDocumentIds: packet.evidenceDocumentIds.join('\n'),
    mustHitEvidence: packet.mustHitEvidence.join('\n'),
    requiredFacts: packet.requiredFacts.join('\n'),
    notes: packet.notes,
    referenceTime: packet.temporalSpec?.referenceTime ?? '',
    targetWindowStart: packet.temporalSpec?.targetWindow.start ?? '',
    targetWindowEnd: packet.temporalSpec?.targetWindow.end ?? '',
    expectedRelation: packet.temporalSpec?.expectedRelation ?? 'latest_before_reference',
    allowAbstain: packet.temporalSpec?.allowAbstain ?? false,
    timeZone: packet.temporalSpec?.timeZone ?? 'Asia/Tokyo',
  }
}

function updateForm(setter: Dispatch<SetStateAction<FormState | null>>, patch: Partial<FormState>) {
  setter((current) => (current ? { ...current, ...patch } : current))
}

function toggleSlice(
  slice: JLocomoBenchmarkSlice,
  setter: Dispatch<SetStateAction<FormState | null>>,
) {
  setter((current) => {
    if (!current) return current
    const exists = current.benchmarkSlices.includes(slice)
    const benchmarkSlices = exists
      ? current.benchmarkSlices.filter((item) => item !== slice)
      : [...current.benchmarkSlices, slice]
    return {
      ...current,
      benchmarkSlices,
    }
  })
}

function parseMultiline(value: string): string[] {
  return [...new Set(value.split('\n').map((line) => line.trim()).filter(Boolean))]
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('ja-JP')
}

const DIFF_FIELD_LABELS: Record<string, string> = {
  reviewStatus: 'レビュー状態',
  query: '質問文',
  expectedAnswer: '正答',
  benchmarkSlices: 'slice',
  evidenceDocumentIds: 'evidence',
  mustHitEvidence: 'must-hit evidence',
  requiredFacts: 'required facts',
  temporalSpec: '時間条件',
  notes: 'メモ',
}

function Field(props: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{props.label}</label>
      {props.children}
    </div>
  )
}

function GuideCard(props: { title: string; body: string }) {
  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="text-sm font-medium">{props.title}</div>
      <div className="mt-2 text-sm text-muted-foreground">{props.body}</div>
    </div>
  )
}

function StatCard(props: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-sm text-muted-foreground">{props.label}</div>
      <div className="mt-2 text-3xl font-semibold">{props.value}</div>
    </div>
  )
}

function MetaCard(props: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="text-xs text-muted-foreground">{props.label}</div>
      <div className="mt-1 text-sm font-medium">{props.value}</div>
    </div>
  )
}

function Badge(props: { label: string; tone?: 'default' | 'primary' }) {
  return (
    <span
      className={`rounded-full px-2 py-1 text-xs ${
        props.tone === 'primary'
          ? 'bg-primary/10 text-primary'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      {props.label}
    </span>
  )
}

function DiffColumn(props: { title: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{props.title}</div>
      <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-foreground">
        {props.value || '（空）'}
      </pre>
    </div>
  )
}

function ReadinessSection(props: { readiness: JLocomoReadinessResponse }) {
  const readiness = props.readiness
  const judgeConfig = readiness.judgeConfiguration

  return (
    <div className="mt-4 space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <MetaCard label="eval packet" value={String(readiness.reviewProgress.evalTotal)} />
        <MetaCard label="eval 裁定済み" value={String(readiness.reviewProgress.eval.adjudicated)} />
        <MetaCard
          label="judge separation"
          value={judgeConfig.judgeSeparated ? '分離済み' : judgeConfig.judgeConfigured ? '未分離' : '未設定'}
        />
        <MetaCard
          label="最新 answer-quality"
          value={readiness.latestSmokeRuns.answerQuality ? formatDate(readiness.latestSmokeRuns.answerQuality.timestamp) : '未実行'}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <MetaCard
          label="generation model"
          value={judgeConfig.generationModel ? `${judgeConfig.generationModel.provider}/${judgeConfig.generationModel.model}` : '不明'}
        />
        <MetaCard
          label="judge model"
          value={judgeConfig.judgeModel ? `${judgeConfig.judgeModel.provider}/${judgeConfig.judgeModel.model}` : '未設定'}
        />
      </div>

      {readiness.blockers.length > 0 ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="text-sm font-medium">完成までの主な blocker</div>
          <ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">
            {readiness.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm">
          readiness 上の major blocker は見つかっていません。残るのは運用上の最終確認です。
        </div>
      )}
    </div>
  )
}

function SmokeModelSummary(props: {
  response: {
    result: {
      provenance?: unknown
    }
  }
}) {
  const signatures = extractSmokeSignatures(props.response.result.provenance)
  if (!signatures) {
    return null
  }

  const sameModel =
    signatures.generationModel?.provider === signatures.judgeModel?.provider &&
    signatures.generationModel?.model === signatures.judgeModel?.model

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <MetaCard
          label="generation model"
          value={signatures.generationModel ? `${signatures.generationModel.provider}/${signatures.generationModel.model}` : '不明'}
        />
        <MetaCard
          label="judge model"
          value={signatures.judgeModel ? `${signatures.judgeModel.provider}/${signatures.judgeModel.model}` : '不明'}
        />
      </div>
      <div
        className={`rounded-md border px-4 py-3 text-sm ${
          sameModel
            ? 'border-amber-300 bg-amber-50'
            : 'border-emerald-300 bg-emerald-50'
        }`}
      >
        {sameModel
          ? '生成役と採点役が同じモデルです。smoke run としては問題ありませんが、本番の benchmark 凍結前には judge を別モデルへ分けるのがおすすめです。'
          : '生成役と採点役は分離されています。評価の独立性としては、こちらの方が望ましい状態です。'}
      </div>
    </div>
  )
}

function extractSmokeSignatures(provenance: unknown): {
  generationModel?: { provider: string; model: string }
  judgeModel?: { provider: string; model: string }
} | null {
  if (!provenance || typeof provenance !== 'object') {
    return null
  }
  const record = provenance as {
    signatures?: {
      generationModel?: { provider?: string; model?: string }
      judgeModel?: { provider?: string; model?: string }
    }
  }
  return {
    generationModel:
      record.signatures?.generationModel?.provider && record.signatures?.generationModel?.model
        ? {
            provider: record.signatures.generationModel.provider,
            model: record.signatures.generationModel.model,
          }
        : undefined,
    judgeModel:
      record.signatures?.judgeModel?.provider && record.signatures?.judgeModel?.model
        ? {
            provider: record.signatures.judgeModel.provider,
            model: record.signatures.judgeModel.model,
          }
        : undefined,
  }
}
